import { useMemo } from 'react'

import { useQuery } from 'react-query'

import { usePagination } from '@webapp/core/hooks/usePagination'
import { type RepoPaginationResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginationResponse'
import { type TravelListStatus } from '@webapp/modules/expense/business-logic/types/TravelListStatus'
import { type TravelStatusCounts } from '@webapp/modules/expense/business-logic/types/TravelStatusCounts'
import { TravelRequestSummary } from '@webapp/modules/expense/entities/TravelRequestSummary'
import { travelQueryConfig } from '@webapp/modules/expense/query-configs/travel'

interface UseQueryApprovableTravelsParams {
  status: TravelListStatus
  subscribedApproverId?: number
  onCountsChange?: (counts: TravelStatusCounts) => void
  onError?: (err: unknown) => void
}

interface UseQueryApprovableTravelsReturn {
  travels: TravelRequestSummary[]
  counts: TravelStatusCounts | undefined
  pagination: RepoPaginationResponse | undefined
  isLoading: boolean
}

export const useQueryApprovableTravels = ({
  status,
  subscribedApproverId,
  onCountsChange,
  onError,
}: UseQueryApprovableTravelsParams): UseQueryApprovableTravelsReturn => {
  const { params, updateNumPages } = usePagination({
    id: 'travel-approval-list',
  })

  const queryParams = {
    search: params.search,
    orderBy: params.orderBy,
    page: params.page,
    pageSize: params.pageSize,
    subscribedApproverId,
  }

  const { data, isLoading } = useQuery({
    ...travelQueryConfig.approvableList(status, queryParams),
    onSuccess: (res) => {
      updateNumPages(res.pagination.numPages)
      onCountsChange?.(res.counts)
    },
    onError,
  })

  const travels = useMemo(
    () => (data?.data ?? []).map((item) => new TravelRequestSummary(item)),
    [data]
  )

  return {
    travels,
    counts: data?.counts,
    pagination: data?.pagination,
    isLoading,
  }
}
